import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

/**
 * ResumeUploader.jsx
 *
 * Props:
 *  - files: Array<File> | FileList | object-like (parent form state)
 *  - setFiles: function(newFilesArray) -> void  OR a wrapper like files => setForm(...)
 *  - errors: object (optional)
 *  - setErrors: function(errorsObj) -> void (optional)
 *  - maxFileSize: number (bytes) default 50 MB
 *
 * Exports:
 *  - default ResumeUploader
 *  - buildFormData(payload, filesArray) helper
 */

const DEFAULT_MAX = 50 * 1024 * 1024;
const WORD_EXTS = ["doc", "docx", "docm", "dot", "dotx", "rtf"];

export default function ResumeUploader({
  files = [],
  setFiles,
  errors = {},
  setErrors = () => {
    //
  },
  maxFileSize = DEFAULT_MAX,
}) {
  const inputRef = useRef(null);
  const dropRef = useRef(null);
  const createdUrlsRef = useRef(new Set()); // track created objectURLs to revoke on unmount
  const [internal, setInternal] = useState([]); // { file, url?, kind }

  // normalize incoming `files` prop to an array
  const normalizeFiles = incoming => {
    if (!incoming) return [];
    if (Array.isArray(incoming)) return incoming;
    try {
      return Array.from(incoming);
    } catch (e) {
      return Object.values(incoming || {});
    }
  };

  const makeKey = f => `${f.name}|${f.size}`;

  // build internal previews from file array, try reuse prevInternal URLs
  const buildInternalFromFiles = (fileArray = [], prevInternal = []) => {
    const seen = new Set();
    const next = [];

    for (const f of fileArray) {
      if (!f || typeof f.name !== "string") continue;
      const key = makeKey(f);
      if (seen.has(key)) continue;
      seen.add(key);

      const prev = prevInternal.find(p => makeKey(p.file) === key);
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      const t = (f.type || "").toLowerCase();
      const isImage = t.startsWith("image/");
      const isPdf = t === "application/pdf" || ext === "pdf";
      const isWordMime =
        t === "application/msword" ||
        t ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        t === "application/vnd.ms-word.document.macroenabled.12" ||
        WORD_EXTS.includes(ext);
      const kind = isImage
        ? "image"
        : isPdf
        ? "pdf"
        : isWordMime
        ? "word"
        : "other";

      let url = null;
      if (isImage) {
        // reuse previous url if available, otherwise create and track it
        if (prev?.url) {
          url = prev.url;
        } else {
          try {
            url = URL.createObjectURL(f);
            createdUrlsRef.current.add(url);
          } catch (e) {
            url = null;
          }
        }
      }

      next.push({ file: f, url, kind });
    }

    // revoke any prevInternal urls that weren't reused
    (prevInternal || []).forEach(p => {
      if (!p) return;
      const k = makeKey(p.file);
      if (!seen.has(k) && p.url) {
        try {
          URL.revokeObjectURL(p.url);
          createdUrlsRef.current.delete(p.url);
        } catch (e) {
          //
        }
      }
    });

    return next;
  };

  // sync internal when parent `files` prop changes
  useEffect(() => {
    const arr = normalizeFiles(files).filter(
      f => f && typeof f.name === "string"
    );
    setInternal(prev => buildInternalFromFiles(arr, prev));
  }, [files]);

  // cleanup all created objectURLs on unmount
  useEffect(() => {
    return () => {
      for (const url of createdUrlsRef.current) {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          //
        }
      }
      createdUrlsRef.current.clear();
    };
  }, []);

  // ---- validation helper ----
  const validateFiles = inFiles => {
    const accepted = [];
    const fileErrors = [];

    (inFiles || []).forEach(f => {
      if (!f || typeof f.name !== "string") return;

      if (f.size > maxFileSize) {
        fileErrors.push(
          `${f.name}: حجم بیش از ${Math.round(maxFileSize / (1024 * 1024))}MB`
        );
        return;
      }
      const t = (f.type || "").toLowerCase();
      const ext = (f.name.split(".").pop() || "").toLowerCase();

      const isImage = t.startsWith("image/");
      const isPdf = t === "application/pdf" || ext === "pdf";
      const isWordMime =
        t === "application/msword" ||
        t ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        t === "application/vnd.ms-word.document.macroenabled.12";
      const isWordExt = WORD_EXTS.includes(ext);

      if (isImage || isPdf || isWordMime || isWordExt) {
        accepted.push(f);
      } else {
        fileErrors.push(`${f.name}: فرمت پشتیبانی نمی‌شود`);
      }
    });

    return { accepted, fileErrors };
  };

  // append files to parent and immediately update internal previews
  const appendFiles = newFiles => {
    const prevList = normalizeFiles(files).filter(
      f => f && typeof f.name === "string"
    );
    const combined = prevList.concat(newFiles || []);

    // dedupe
    const seen = new Set();
    const deduped = [];
    for (const f of combined) {
      if (!f || typeof f.name !== "string") continue;
      const k = makeKey(f);
      if (!seen.has(k)) {
        seen.add(k);
        deduped.push(f);
      }
    }

    // try setFiles(deduped) — support both setter and wrapper
    try {
      setFiles(deduped);
    } catch (e) {
      //
    }

    // update internal immediately so preview shows without waiting parent re-render
    setInternal(prevInternal => buildInternalFromFiles(deduped, prevInternal));
  };

  // remove file at index (update parent and internal)
  const removeAt = idx => {
    if (idx < 0 || idx >= files?.length) return;

    setFiles([...files.filter((file, index) => idx !== index)]);

    setErrors(cur => {
      const c = { ...cur };
      if (c.files) delete c.files;
      return c;
    });
  };

  // file input change handler
  const onFilesSelected = e => {
    const chosen = Array.from(e.target.files || []);
    if (chosen.length === 0) return;
    const { accepted, fileErrors } = validateFiles(chosen);
    if (fileErrors.length > 0) {
      setErrors(cur => ({ ...cur, files: fileErrors.join(" ، ") }));
    } else {
      setErrors(cur => {
        const c = { ...cur };
        delete c.files;
        return c;
      });
    }
    if (accepted.length > 0) appendFiles(accepted);
    // reset input so same file can be picked again
    e.target.value = "";
  };

  // drag & drop handlers
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;

    const onDragOver = ev => {
      ev.preventDefault();
      ev.dataTransfer.dropEffect = "copy";
      el.classList.add("ru-dragover");
    };
    const onDragLeave = () => {
      el.classList.remove("ru-dragover");
    };
    const onDrop = ev => {
      ev.preventDefault();
      el.classList.remove("ru-dragover");
      const dropped = Array.from(ev.dataTransfer.files || []);
      if (dropped.length === 0) return;
      const { accepted, fileErrors } = validateFiles(dropped);
      if (fileErrors.length > 0) {
        setErrors(cur => ({ ...cur, files: fileErrors.join(" ، ") }));
      } else {
        setErrors(cur => {
          const c = { ...cur };
          delete c.files;
          return c;
        });
      }
      if (accepted.length > 0) appendFiles(accepted);
    };

    el.addEventListener("dragover", onDragOver);
    el.addEventListener("dragleave", onDragLeave);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragover", onDragOver);
      el.removeEventListener("dragleave", onDragLeave);
      el.removeEventListener("drop", onDrop);
    };
  }, [dropRef.current, files]);

  // helper: human readable size
  const humanSize = bytes => {
    if (!bytes || Number.isNaN(Number(bytes))) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // styles (lightweight inline)
  const styles = useMemo(
    () => ({
      wrapper: {
        border: "1px dashed #d0d7de",
        borderRadius: 8,
        padding: 12,
        background: "#fff",
      },
      drop: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "12px 14px",
        borderRadius: 8,
        cursor: "pointer",
        transition: "background .15s, border-color .15s, box-shadow .15s",
      },
      input: { display: "none" },
      grid: {
        marginTop: 12,
        display: "grid",
        gap: 10,
        gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
      },
      tile: {
        position: "relative",
        borderRadius: 8,
        overflow: "hidden",
        border: "1px solid #e6edf3",
        background: "#fafbfc",
        minHeight: 100,
        display: "flex",
        alignItems: "stretch",
        flexDirection: "column",
      },
      previewImage: {
        width: "100%",
        height: 84,
        objectFit: "cover",
        display: "block",
        background: "#f4f6f8",
      },
      tileBody: {
        padding: "8px 10px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
      },
      name: {
        fontSize: 13,
        lineHeight: 1.2,
        color: "#111827",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      small: { fontSize: 12, color: "#6b7280" },
      removeBtn: {
        border: "none",
        background: "transparent",
        color: "#ef4444",
        cursor: "pointer",
        padding: 6,
        fontSize: 13,
      },
      badgeCenter: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: 84,
        background: "#f3f4f6",
        fontSize: 14,
        color: "#374151",
        fontWeight: 600,
      },
      iconWrap: {
        width: 36,
        height: 36,
        borderRadius: 6,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#fff",
        border: "1px solid #e5e7eb",
        marginRight: 8,
      },
    }),
    []
  );

  // icons
  const PdfIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlSpace="preserve"
      viewBox="0 0 512 512"
      width={45}
    >
      <path
        d="M128 0c-17.6 0-32 14.4-32 32v448c0 17.6 14.4 32 32 32h320c17.6 0 32-14.4 32-32V128L352 0H128z"
        style={{
          fill: "#e2e5e7",
        }}
      />
      <path
        d="M384 128h96L352 0v96c0 17.6 14.4 32 32 32z"
        style={{
          fill: "#b0b7bd",
        }}
      />
      <path
        d="m480 224-96-96h96z"
        style={{
          fill: "#cad1d8",
        }}
      />
      <path
        d="M416 416c0 8.8-7.2 16-16 16H48c-8.8 0-16-7.2-16-16V256c0-8.8 7.2-16 16-16h352c8.8 0 16 7.2 16 16v160z"
        style={{
          fill: "#f15642",
        }}
      />
      <path
        d="M101.744 303.152c0-4.224 3.328-8.832 8.688-8.832h29.552c16.64 0 31.616 11.136 31.616 32.48 0 20.224-14.976 31.488-31.616 31.488h-21.36v16.896c0 5.632-3.584 8.816-8.192 8.816-4.224 0-8.688-3.184-8.688-8.816v-72.032zm16.88 7.28v31.872h21.36c8.576 0 15.36-7.568 15.36-15.504 0-8.944-6.784-16.368-15.36-16.368h-21.36zM196.656 384c-4.224 0-8.832-2.304-8.832-7.92v-72.672c0-4.592 4.608-7.936 8.832-7.936h29.296c58.464 0 57.184 88.528 1.152 88.528h-30.448zm8.064-72.912V368.4h21.232c34.544 0 36.08-57.312 0-57.312H204.72zM303.872 312.112v20.336h32.624c4.608 0 9.216 4.608 9.216 9.072 0 4.224-4.608 7.68-9.216 7.68h-32.624v26.864c0 4.48-3.184 7.92-7.664 7.92-5.632 0-9.072-3.44-9.072-7.92v-72.672c0-4.592 3.456-7.936 9.072-7.936h44.912c5.632 0 8.96 3.344 8.96 7.936 0 4.096-3.328 8.704-8.96 8.704h-37.248v.016z"
        style={{
          fill: "#fff",
        }}
      />
      <path
        d="M400 432H96v16h304c8.8 0 16-7.2 16-16v-16c0 8.8-7.2 16-16 16z"
        style={{
          fill: "#cad1d8",
        }}
      />
    </svg>
  );
  const WordIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="#FFF"
      strokeMiterlimit="10"
      strokeWidth="2"
      viewBox="0 0 96 96"
      width={45}
    >
      <path
        stroke="#979593"
        d="M67.1716 7H27c-1.1046 0-2 .8954-2 2v78c0 1.1046.8954 2 2 2h58c1.1046 0 2-.8954 2-2V26.8284c0-.5304-.2107-1.0391-.5858-1.4142L68.5858 7.5858C68.2107 7.2107 67.702 7 67.1716 7z"
      />
      <path fill="none" stroke="#979593" d="M67 7v18c0 1.1046.8954 2 2 2h18" />
      <path
        fill="#C8C6C4"
        d="M79 61H48v-2h31c.5523 0 1 .4477 1 1s-.4477 1-1 1zm0-6H48v-2h31c.5523 0 1 .4477 1 1s-.4477 1-1 1zm0-6H48v-2h31c.5523 0 1 .4477 1 1s-.4477 1-1 1zm0-6H48v-2h31c.5523 0 1 .4477 1 1s-.4477 1-1 1zm0 24H48v-2h31c.5523 0 1 .4477 1 1s-.4477 1-1 1z"
      />
      <path
        fill="#185ABD"
        d="M12 74h32c2.2091 0 4-1.7909 4-4V38c0-2.2091-1.7909-4-4-4H12c-2.2091 0-4 1.7909-4 4v32c0 2.2091 1.7909 4 4 4z"
      />
      <path d="M21.6245 60.6455c.0661.522.109.9769.1296 1.3657h.0762c.0306-.3685.0889-.8129.1751-1.3349.0862-.5211.1703-.961.2517-1.319L25.7911 44h4.5702l3.6562 15.1272c.183.7468.3353 1.6973.457 2.8532h.0608c.0508-.7979.1777-1.7184.3809-2.7615L37.8413 44H42l-5.1183 22h-4.86l-3.4885-14.5744c-.1016-.4197-.2158-.9663-.3428-1.6417-.127-.6745-.2057-1.1656-.236-1.4724h-.0608c-.0407.358-.1195.8896-.2364 1.595-.1169.7062-.211 1.2273-.2819 1.565L24.1 66h-4.9357L14 44h4.2349l3.1843 15.3882c.0709.3165.1392.7362.2053 1.2573z" />
    </svg>
  );

  return (
    <div style={styles.wrapper}>
      <label style={{ fontWeight: 600, display: "block", marginBottom: 8 }}>
        رزومه / مستندات (اختیاری)
      </label>

      <div
        ref={dropRef}
        style={{ ...styles.drop, border: "1px dashed #d1d5db" }}
        onClick={() => inputRef.current && inputRef.current.click()}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ")
            inputRef.current && inputRef.current.click();
        }}
        className="ru-dropzone"
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={styles.iconWrap}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="1em"
              height="1em"
            >
              <path
                fill="currentColor"
                d="M19.35 10.04A7.49 7.49 0 0 0 12 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 0 0 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5c0-2.64-2.05-4.78-4.65-4.96M19 18H6c-2.21 0-4-1.79-4-4c0-2.05 1.53-3.76 3.56-3.97l1.07-.11l.5-.95A5.47 5.47 0 0 1 12 6c2.62 0 4.88 1.86 5.39 4.43l.3 1.5l1.53.11A2.98 2.98 0 0 1 22 15c0 1.65-1.35 3-3 3M8 13h2.55v3h2.9v-3H16l-4-4z"
              />
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 14, color: "#111827" }}>
              فایل‌ها را اینجا رها کنید یا کلیک کنید
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              PDF, Word, تصاویر — هر فایل تا{" "}
              {Math.round(maxFileSize / (1024 * 1024))}MB
            </div>
          </div>
        </div>

        <div style={{ fontSize: 13, color: "#2563eb" }}>انتخاب فایل</div>

        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx,.docm,.dot,.dotx,.rtf,image/*"
          style={styles.input}
          onChange={onFilesSelected}
        />
      </div>

      {errors.files && (
        <div style={{ marginTop: 8, color: "#dc2626", fontSize: 13 }}>
          {errors.files}
        </div>
      )}

      <div style={styles.grid}>
        {internal.map((it, i) => {
          const filename = it?.file?.name ?? "نام‌نامشخص";
          const filesize = Number(it?.file?.size || 0);
          const kind = it?.kind ?? "other";
          const url = it?.url ?? null;

          return (
            <div key={`${filename}-${filesize}-${i}`} style={styles.tile}>
              {kind === "image" && url ? (
                <img src={url} alt={filename} style={styles.previewImage} />
              ) : (
                <div style={styles.badgeCenter}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ marginBottom: 6 }}>
                      {kind === "pdf" ? <PdfIcon /> : <WordIcon />}
                    </div>
                    <div style={{ fontSize: 13 }}>
                      {(filename.split(".").pop() || "").toUpperCase()}
                    </div>
                  </div>
                </div>
              )}

              <div style={styles.tileBody}>
                <div style={{ flex: 1, overflow: "hidden" }}>
                  <div style={styles.name} title={filename}>
                    {filename}
                  </div>
                  <div style={styles.small}>{humanSize(filesize)}</div>
                </div>
                <button
                  type="button"
                  aria-label={`حذف ${filename}`}
                  title="حذف"
                  onClick={() => removeAt(i)}
                  style={styles.removeBtn}
                >
                  ✕
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

ResumeUploader.propTypes = {
  files: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  setFiles: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setErrors: PropTypes.func,
  maxFileSize: PropTypes.number,
};
